import React, { useContext, useEffect, useState } from 'react';
import { PlusIcon, EllipsisVerticalIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Badge } from '../../../components/Badge';
import { Link } from '../../../components/Link';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Divider } from '../../../components/Divider';
import { Avatar } from '../../../components/Avatar';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination'; // Import the Pagination component
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { useSetupBrand } from '../../../hooks/useSetupBrand';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { UserContext } from '../../../context/userContext';
import { SetupBrand } from '../../../models/SetupBrand';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';

const COLUMNS = [
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'created', label: 'Created on' },
    { key: 'updated', label: 'Updated on' },
];
const SORT_OPTIONS = [
    { label: 'Name (A-Z)', value: 'az' },
    { label: 'Name (Z-A)', value: 'za' },
];

const SetupBrandPage: React.FC = () => {
    const { currentUser } = useContext(UserContext); // Get current user info
    const { loading, error, deleteBrand, fetchBrands, brands } = useSetupBrand();
    const [searchQuery, setSearchQuery] = useState<string>(''); // State to store the search query
    const [isOpen, setIsOpen] = useState(false); // Dialog open state
    const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion
    const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null); // ID of the brand to be deleted
    const [sortOrder, setSortOrder] = useState('az');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const navigate = useNavigate();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterWarehouseName, setFilterWarehouseName] = useState('');

    useEffect(() => {
        document.title = "Brands";
    }, []);

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseName('');
    };

    const availableYears = Array.from(
        new Set(
            brands
                .map((b) => new Date(b.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const availableWarehouseNames = Array.from(
        new Set(brands.map((b) => b.warehouse_name).filter((name): name is string => !!name))
    ).sort();

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterWarehouseName].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterWarehouseName ? [{ key: 'warehouse', label: `Warehouse: ${filterWarehouseName}`, onClear: () => setFilterWarehouseName('') }] : []),
    ];

    // Filter brands based on the search query
    const filteredBrand = brands.filter((brand) => {
        const matchesSearch = brand.name!.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStartDate = !filterStartDate || new Date(brand.created_time) >= new Date(filterStartDate);
        const matchesEndDate = !filterEndDate || new Date(brand.created_time) <= new Date(filterEndDate);
        const matchesYear = !filterYear || new Date(brand.created_time).getFullYear().toString() === filterYear;
        const matchesWarehouse = !filterWarehouseName || brand.warehouse_name === filterWarehouseName;
        return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
    });

    const handleOpenDeleteDialog = (brandId: string) => {
        setSelectedBrandId(brandId);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedBrandId) return;

        setIsDeleting(true);
        try {
            await deleteBrand(Number(selectedBrandId));
            fetchBrands();
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete brand:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Brands</Heading>
                </div>
                <Button onClick={() => navigate('/gp/inventories/brands/add')}><PlusIcon />Create new brand</Button>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search brand…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    <TableHeader>Brand Name</TableHeader>
                                    {isColumnVisible(columnVisibility, 'warehouse') && <TableHeader>Warehouse</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created on</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'updated') && <TableHeader>Updated on</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(filteredBrand.length > 0) ? [...filteredBrand].sort((a, b) => sortOrder === 'az' ? a.name!.localeCompare(b.name!) : b.name!.localeCompare(a.name!)).map((brand, index) => (
                                    <TableRow key={brand.id} title={`${brand.name}`}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{brand.name}</TableCell>
                                        {isColumnVisible(columnVisibility, 'warehouse') && <TableCell className="text-zinc-500">{brand.warehouse_name || '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'created') && <TableCell className="text-zinc-500">{dateFormat(brand.created_time?.toString(), "mmm dd, yyyy")}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'updated') && <TableCell className="text-zinc-500">{dateFormat(brand.updated_time?.toString(), "mmm dd, yyyy")}</TableCell>}
                                        <TableCell className="py-5 flex gap-4 text-zinc-500 justify-center">
                                            <a onClick={() => navigate(`/gp/inventories/brands/edit/${brand.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <PencilIcon className='h-5 w-5' />
                                            </a>
                                            <a onClick={() => navigate(`/gp/inventories/brands/view/${brand.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                            <Dropdown>
                                                <DropdownButton plain aria-label="More options" className='h-5 w-5' >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom end">
                                                    <DropdownItem onClick={() => handleOpenDeleteDialog(brand.id.toString())}>Delete</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No brands found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {availableWarehouseNames.length > 0 && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseName} onChange={(e) => setFilterWarehouseName(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {availableWarehouseNames.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Brand</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this brand? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SetupBrandPage;
